import { useState } from 'react';
import { useParams } from 'react-router';
import { Trans, useLingui } from '@lingui/react/macro';

import { GenericError } from '@/components/GenericError/GenericError';
import { useNavigate } from '@/components/Link/Link';
import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import ConfirmationDialog from '@/design_system/Dialog/ConfirmationDialog';
import Stack from '@/design_system/Stack';
import { useShowToast } from '@/design_system/Toast';
import EditableTitle from '@/design_system/Typography/EditableTitle';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import { useDeleteRole, useRole, useUpdateRole } from '@/models/role';
import { ErrorState } from '@/routes/SettingsBeta/components/ErrorState';
import { SettingsBreadcrumbParent } from '@/routes/SettingsBeta/components/Layout/Layout';
import { Loading } from '@/routes/SettingsBeta/components/Loading';
import { NotFoundError } from '@/utils/error/NotFoundError';

import { RoleForm } from '../RoleForm';

export const Role = () => {
  const { id } = useParams();
  const { t } = useLingui();
  const showToast = useShowToast();
  const { mutateAsync: updateRole, isPending: isUpdateRoleNamePending } = useUpdateRole();
  const { data: role, isLoading, isError, error } = useRole(id);

  if (isLoading) {
    return <Loading />;
  }

  if (isError || !role) {
    let errorMessage = <GenericError />;

    if (error instanceof NotFoundError) {
      errorMessage = (
        <Paragraph>
          <Trans id="settings.roles.detail.not-found">
            Unknown role or you do not have access to it.
          </Trans>
        </Paragraph>
      );
    }

    return <ErrorState errorMessage={errorMessage} />;
  }

  const handleSaveRoleName = async (roleName: string) => {
    if (!id) {
      return;
    }

    try {
      await updateRole({
        roleId: id,
        name: roleName,
      });

      showToast({
        text: t({
          id: 'settings.roles.update.success',
          message: `Role "${roleName}" updated successfully`,
        }),
        type: 'success',
      });
    } catch {
      showToast({
        text: (
          <Trans id="settings.roles.detail.rename.error">
            Could not rename the role. Please try again.
          </Trans>
        ),
        type: 'error',
      });
    }
  };

  return (
    <main>
      <Stack gap="1.5rem">
        <Stack
          row
          className="settings-section__record-header"
          gap="0.5rem"
          alignItems="center"
          flexWrap="wrap"
        >
          <SettingsBreadcrumbParent
            to="/settings_beta/team/roles"
            label={<Trans id="settings.organization.members.roles">Roles</Trans>}
          />
          <EditableTitle
            value={role.name}
            onSave={handleSaveRoleName}
            isSaving={isUpdateRoleNamePending}
            titleProps={{ as: 'h1', size: '200', weight: 'medium' }}
            editAriaLabel={t({
              id: 'settings.roles.detail.editable-title.edit',
              message: 'Edit role name',
            })}
            saveAriaLabel={t({
              id: 'settings.roles.detail.editable-title.save',
              message: 'Save role name',
            })}
            cancelAriaLabel={t({
              id: 'settings.roles.detail.editable-title.cancel',
              message: 'Cancel renaming',
            })}
            inputAriaLabel={t({
              id: 'settings.roles.detail.editable-title.input',
              message: 'Role name',
            })}
          />
        </Stack>

        <RoleForm role={role} />

        <Title as="h3">
          <Trans id="settings.roles.detail.settings.title">Settings</Trans>
        </Title>
        <Box style={{ flex: 'unset' }}>
          <Stack row gap="1rem" alignItems="center" justifyContent="space-between">
            <Stack flexWrap="wrap">
              <Paragraph weight="bold">
                <Trans id="settings.roles.detail.settings.delete.title">Delete this role</Trans>
              </Paragraph>
              <Paragraph size="200" color="secondary">
                <Trans id="settings.roles.detail.settings.delete.cannot-be-undone">
                  This action cannot be undone.
                </Trans>
              </Paragraph>
            </Stack>
            <DeleteRole roleId={role.id} roleName={role.name} />
          </Stack>
        </Box>
      </Stack>
    </main>
  );
};

const DeleteRole = ({ roleId, roleName }: { roleId: string; roleName: string }) => {
  const { t } = useLingui();
  const showToast = useShowToast();
  const navigate = useNavigate();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const { mutateAsync: deleteRole, isPending: isDeleteRolePending } = useDeleteRole();

  const handleConfirmDelete = () => {
    deleteRole(roleId)
      .then(() => {
        showToast({
          text: (
            <Trans id="settings.roles.delete.confirmation.success">
              The role <b>&quot;{roleName}&quot;</b> has been deleted.
            </Trans>
          ),
          type: 'success',
        });
        navigate('/settings_beta/team/roles');
      })
      .catch(() => {
        showToast({
          text: (
            <Trans id="settings.roles.delete.confirmation.error">
              Error while deleting the role <b>&quot;{roleName}&quot;</b>. Please try again later.
            </Trans>
          ),
          type: 'error',
        });
      })
      .finally(() => {
        setShowDeleteConfirmation(false);
      });
  };

  return (
    <>
      <Button
        variant="secondary-danger"
        size="small"
        isLoading={isDeleteRolePending}
        onPress={() => setShowDeleteConfirmation(true)}
        ariaLabel={t({
          id: 'settings.roles.delete.trigger',
          message: `Delete ${roleName}`,
        })}
      >
        <Trans id="settings.roles.detail.settings.delete.button">Delete role</Trans>
      </Button>
      <ConfirmationDialog
        showDialog={showDeleteConfirmation}
        setShowDialog={setShowDeleteConfirmation}
        title={<Trans id="settings.roles.delete.confirmation.title">Delete role</Trans>}
        confirm={<Trans id="settings.roles.delete.confirmation.confirm">Delete</Trans>}
        onConfirm={handleConfirmDelete}
        maxWidth="35rem"
        isPending={isDeleteRolePending}
      >
        <Paragraph>
          <Trans id="settings.roles.delete.confirmation.description">
            Are you sure you want to delete the role &quot;{roleName}&quot;? This action cannot be
            undone.
          </Trans>
        </Paragraph>
      </ConfirmationDialog>
    </>
  );
};
